import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { WorkspaceId } from "@dayvise/domain";
import { Button, Dialog, RunStateBadge, Spinner, stackStyle, rowStyle, MUTED_TEXT } from "@dayvise/react-ui";
import { useActiveWorkspaceId, useDayviseClient, useSession } from "./providers.tsx";
import { queryKeys } from "./queryKeys.ts";

function AgentActivityDialog({ workspaceId, readerKey, onClose }: {
  readonly workspaceId: WorkspaceId;
  readonly readerKey: string;
  readonly onClose: () => void;
}) {
  const client = useDayviseClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const agents = useQuery({
    queryKey: [...queryKeys.agents({ workspaceId }), "activity", readerKey],
    queryFn: ({ signal }) => client.agents.list({ signal }),
    gcTime: 0,
    retry: false,
    refetchInterval: 5_000,
  });
  const runs = useQuery({
    queryKey: [...queryKeys.runs({ workspaceId }), "activity", readerKey],
    queryFn: async ({ signal }) => {
      const [recent, active] = await Promise.all([client.runs.list({ signal }), client.runs.active({ signal })]);
      const byId = new Map(recent.runs.map((run) => [run.runId, run]));
      for (const run of active.runs) byId.set(run.runId, run);
      return { runs: [...byId.values()] };
    },
    gcTime: 0,
    retry: false,
    refetchInterval: 5_000,
  });
  const visibleAgents = (agents.data?.agents ?? []).filter((agent) => agent.workspaceId === workspaceId && agent.archivedAt === null);
  const recentRuns = (runs.data?.runs ?? []).filter((run) => run.workspaceId === workspaceId);
  const selected = visibleAgents.find((agent) => agent.agentId === selectedId);
  const activity = selected ? recentRuns.filter((run) => run.agentId === selected.agentId) : [];
  const failed = agents.isError || runs.isError;
  const loading = agents.isPending || runs.isPending;
  const refreshing = agents.isFetching || runs.isFetching;
  return (
    <Dialog open title="Agents" onClose={onClose} description="Authorized agents and recent run progress. This view does not start or modify runs."
      footer={<Button disabled={refreshing} onClick={() => { void agents.refetch(); void runs.refetch(); }}>Refresh activity</Button>}>
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        {loading ? <Spinner label="Loading agent activity" /> : null}
        {failed ? <p role="alert">Unable to load agent activity. Refresh to try again.</p> : null}
        {!failed && !loading ? <>
          {visibleAgents.length === 0 ? <p>No agents available in this workspace.</p> : null}
          <div style={rowStyle("var(--dayvise-space-xs)", "flex-start")}>
            {visibleAgents.map((agent) => (
              <Button key={agent.agentId} variant="secondary" aria-pressed={selectedId === agent.agentId} onClick={() => setSelectedId(agent.agentId)}>
                {agent.name} · {recentRuns.filter((run) => run.agentId === agent.agentId).length} visible runs
              </Button>
            ))}
          </div>
          {!selected && visibleAgents.length > 0 ? <p>Select an agent to inspect its progress.</p> : null}
          {selected ? <section aria-label={`${selected.name} activity`} style={stackStyle("var(--dayvise-space-xs)")}>
            <h3 style={{ margin: 0 }}>{selected.name}</h3>
            {selected.description ? <p>{selected.description}</p> : null}
            <p style={MUTED_TEXT}>{selected.shared ? "Shared agent" : "Private agent"} · {selected.modelProvider ?? "Default provider"} · {selected.modelName ?? "Default model"}</p>
            <p style={MUTED_TEXT}>Active and recent authorized runs, including up to 50 recent workspace runs. State and usage are recorded progress, not a completion percentage.</p>
            {activity.length === 0 ? <p>No active or recent authorized runs for this agent.</p> : null}
            <ul style={{ margin: 0, paddingInlineStart: "var(--dayvise-space-lg)" }}>
              {activity.map((run) => <li key={run.runId} style={{ marginBottom: "var(--dayvise-space-sm)" }}>
                <RunStateBadge state={run.state} /> <span>{run.runId}</span>
                <p>Branch {run.branchId} · Attempt {run.attempt}/{run.maxAttempts}</p>
                {run.pauseReason ? <p>Waiting: {run.pauseReason}</p> : null}
                <p>{run.usage.toolCalls} tool calls · {run.usage.inputTokens} input tokens · {run.usage.outputTokens} output tokens</p>
                <p style={MUTED_TEXT}>Last recorded update: {run.updatedAt}</p>
              </li>)}
            </ul>
          </section> : null}
        </> : null}
      </div>
    </Dialog>
  );
}

export function useAgentActivityDialog(contextKey: string) {
  const workspaceId = useActiveWorkspaceId();
  const { session } = useSession();
  const userId = session.account?.userId ?? null;
  const readerKey = JSON.stringify([session.account?.identityId ?? null, userId]);
  const scopeKey = JSON.stringify([workspaceId, readerKey, contextKey]);
  const [binding, setBinding] = useState({ scopeKey, open: false });
  if (binding.scopeKey !== scopeKey) setBinding({ scopeKey, open: false });
  const close = () => setBinding({ scopeKey, open: false });
  const open = () => {
    if (!workspaceId || !userId) throw new Error("Sign in and select a workspace before viewing agents.");
    setBinding({ scopeKey, open: true });
  };
  const dialog = binding.open && binding.scopeKey === scopeKey && workspaceId && userId
    ? <AgentActivityDialog key={scopeKey} workspaceId={workspaceId} readerKey={readerKey} onClose={close} /> : null;
  return { open, close, dialog };
}
